// src/modules/stores/hooks/useStores.ts
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
  useMemo,
} from "react";
import { AuthContext } from "@modules/auth/contexts/auth-plus.context";
import { StoreService } from "../services/store.service";
import {
  StoreDto,
  ShopDto,
  CreateStoreInput,
  UpdateStoreInput,
  CreateShopInput,
  UpdateShopInput,
} from "../models/store.model";
import { updateEntityInList } from "@modules/shared/utils/state-updates";

export function useStores(storeId?: string) {
  const { auth, setAuth, logout } = useContext(AuthContext);
  const service = useRef(new StoreService()).current;

  // List state
  const [stores, setStores] = useState<StoreDto[]>([]);
  const [shops, setShops] = useState<ShopDto[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listLoaded, setListLoaded] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Detail state
  const [currentStore, setCurrentStore] = useState<StoreDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoaded, setDetailLoaded] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const listLoadingRef = useRef(false);
  const listLoadedRef = useRef(false);
  const authArgs = useMemo(
    () => ({ auth, setAuth, logout }),
    [auth, setAuth, logout],
  );

  const isStoreListRoute = !storeId; // Không có storeId -> trang list
  const isStoreDetailRoute = !!storeId; // Có storeId -> trang detail

  // ---- Load danh sách stores ----
  const loadAll = useCallback(async () => {
    if (!auth || listLoadingRef.current || listLoadedRef.current) return;
    listLoadingRef.current = true;
    setListLoading(true);
    setListError(null);

    try {
      const [storeData, shopData] = await Promise.all([
        service.getAllStores(authArgs),
        service.getAllShops(authArgs),
      ]);
      setStores(storeData);
      setShops(shopData);
    } catch (err) {
      setListError(
        err instanceof Error ? err.message : "Failed to load stores",
      );
    } finally {
      listLoadedRef.current = true;
      setListLoaded(true);
      setListLoading(false);
      listLoadingRef.current = false;
    }
  }, [auth, authArgs, service]);

  // ---- Load chi tiết một store ----
  const loadStoreDetail = useCallback(
    async (id: string) => {
      if (!auth || !id) return;
      setDetailLoading(true);
      setDetailError(null);

      try {
        const store = await service.getStoreById(id, authArgs);
        if (!store) {
          setDetailError("Không tìm thấy nhãn hàng");
          setCurrentStore(null);
          return;
        }
        setCurrentStore(store);

        // Thêm vào list nếu chưa có
        setStores((prev) => {
          const exists = prev.some((s) => s.id === store.id);
          if (!exists) return [...prev, store];
          return prev.map((s) => (s.id === store.id ? store : s));
        });

        // Load shops của store này
        const shopData = await service.getShopsByStore(store.id, authArgs);
        setShops((prev) => {
          // Giữ các shop khác và cập nhật/ thêm mới shops của store này
          const otherShops = prev.filter((s) => s.b2b_id !== store.id);
          return [...otherShops, ...shopData];
        });

        setDetailLoaded(true);
      } catch (err) {
        setDetailError(
          err instanceof Error
            ? err.message
            : "Không thể tải chi tiết nhãn hàng",
        );
        setCurrentStore(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [auth, authArgs, service],
  );

  // ---- Effect: load list khi ở trang list ----
  useEffect(() => {
    if (isStoreDetailRoute) {
      // Nếu ở detail, không load list
      setListLoaded(true);
      return;
    }
    if (!auth) {
      setStores([]);
      setShops([]);
      setListLoaded(true);
      setListError(null);
      listLoadedRef.current = false;
      listLoadingRef.current = false;
      return;
    }
    loadAll();
  }, [auth, isStoreDetailRoute, isStoreListRoute, loadAll]);

  // ---- Actions ----
  const createStore = useCallback(
    async (input: CreateStoreInput) => {
      const newStore = await service.createStore(input, authArgs);
      setStores((prev) => updateEntityInList(prev, newStore));
      return newStore;
    },
    [service, authArgs],
  );

  const updateStore = useCallback(
    async (id: string, input: UpdateStoreInput) => {
      const updated = await service.updateStore(id, input, authArgs);
      setStores((prev) => updateEntityInList(prev, updated));
      // Nếu đang ở detail, cập nhật currentStore
      if (currentStore?.id === id) {
        setCurrentStore(updated);
      }
      return updated;
    },
    [service, authArgs, currentStore],
  );

  const deleteStore = useCallback(
    async (id: string) => {
      const success = await service.deleteStore(id, authArgs);
      if (success) setStores((prev) => prev.filter((s) => s.id !== id));
      return success;
    },
    [service, authArgs],
  );

  const createShop = useCallback(
    async (input: CreateShopInput) => {
      const newShop = await service.createShop(input, authArgs);
      setShops((prev) => updateEntityInList(prev, newShop));
      return newShop;
    },
    [service, authArgs],
  );

  const updateShop = useCallback(
    async (input: UpdateShopInput) => {
      const updated = await service.updateShop(input, authArgs);
      setShops((prev) => updateEntityInList(prev, updated));
      return updated;
    },
    [service, authArgs],
  );

  const deleteShop = useCallback(
    async (shopId: string) => {
      const success = await service.deleteShop(shopId, authArgs);
      if (success) setShops((prev) => prev.filter((s) => s.id !== shopId));
      return success;
    },
    [service, authArgs],
  );

  const refresh = useCallback(async () => {
    listLoadedRef.current = false;
    setListLoaded(false);
    await loadAll();
  }, [loadAll]);

  const getStoreById = useCallback(
    (id: string) =>
      stores.find((s) => s.id === id) || currentStore || undefined,
    [stores, currentStore],
  );

  const getShopById = useCallback(
    (id: string) => shops.find((s) => s.id === id),
    [shops],
  );

  return {
    // List
    stores,
    shops,
    loading: listLoading,
    loaded: listLoaded,
    error: listError,

    // Detail
    currentStore,
    detailLoading,
    detailLoaded,
    detailError,
    loadStoreDetail,

    // Actions
    createStore,
    updateStore,
    deleteStore,
    createShop,
    updateShop,
    deleteShop,
    refresh,
    getStoreById,
    getShopById,
  };
}
